import CryptoJS from "crypto-js";

interface AzulPaymentConfig {
  merchantId: string;
  merchantName: string;
  merchantType: string;
  currencyCode: string;
  orderNumber: string;
  itbis: string;
  approvedUrl: string;
  declinedUrl: string;
  cancelUrl: string;
  useCustomField1: string;
  useCustomField2: string;
  customOrderId?: string;
  logoImageUrl?: string;
  authKey: string;
}

interface AzulPaymentData {
  amount: number; // Monto en dólares
  orderNumber?: string;
  userId?: string | null;
  branchId?: string | null;
  customOrderId?: string; // Identificador único de la transacción para VerifyPayment (opcional, X(75))
}

class AzulPaymentService {
  /**
   * Obtiene la URL base de la aplicación
   */
  private getBaseUrl(): string {
    if (typeof window !== "undefined") {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return "";
  }

  /**
   * Obtiene la configuración del pago con URLs dinámicas
   */
  private getConfig(userId?: string | null, branchId?: string | null, customOrderId?: string): AzulPaymentConfig {
    const baseUrl = this.getBaseUrl();
    const logoImageUrl = `${baseUrl}/logo.png`;
    
    // Construir URLs con parámetros si están disponibles
    let approvedUrl = `${baseUrl}/confirmation`;
    let declinedUrl = `${baseUrl}/payment/declined`;
    let cancelUrl = `${baseUrl}/payment/cancelled`;
    
    if (userId && branchId) {
      approvedUrl += `?userId=${userId}&branchId=${branchId}`;
      declinedUrl += `?userId=${userId}&branchId=${branchId}`;
      cancelUrl += `?userId=${userId}&branchId=${branchId}`;
    }
    
    return {
      merchantId: "39038540035",
      merchantName: "Divix",
      merchantType: "ECommerce",
      currencyCode: "$",
      orderNumber: "1234",
      // TODO: verificar si el itbis se saca del amount o si va 000
      // Por detras parece que azul chequea que itbis < amount
      itbis: "000",
      approvedUrl: approvedUrl,
      declinedUrl: declinedUrl,
      cancelUrl: cancelUrl,
      useCustomField1: "0",
      useCustomField2: "0",
      customOrderId: customOrderId,
      logoImageUrl: logoImageUrl,
      authKey: "asdhakjshdkjasdasmndajksdkjaskldga8odya9d8yoasyd98asdyaisdhoaisyd0a8sydoashd8oasydoiahdpiashd09ayusidhaos8dy0a8dya08syd0a8ssdsax",
    };
  }

  private readonly formId = "paymentForm";
  private readonly azulPaymentUrl = "https://pruebas.azul.com.do/PaymentPage/";

  /**
   * Calcula el hash de autenticación para Azul
   */
  private calculateAuthHash(amountInCents: string, orderNumber: string, userId?: string | null, branchId?: string | null, customOrderId?: string): string {
    const config = this.getConfig(userId, branchId, customOrderId);
    const {
      merchantId,
      merchantName,
      merchantType,
      currencyCode,
      itbis,
      approvedUrl,
      declinedUrl,
      cancelUrl,
      useCustomField1,
      useCustomField2,
      authKey,
    } = config;

    let cadena =
      merchantId +
      merchantName +
      merchantType +
      currencyCode +
      orderNumber +
      amountInCents +
      itbis +
      approvedUrl +
      declinedUrl +
      cancelUrl +
      useCustomField1 +
      useCustomField2;    
    
    cadena += authKey;

    return CryptoJS.HmacSHA512(cadena, authKey).toString(CryptoJS.enc.Hex);
  }

  /**
   * Convierte el monto de dólares a centavos (formato requerido por Azul)
   */
  private convertToCents(amount: number): string {
    return (amount * 100).toString();
  }

  /**
   * Actualiza los campos del formulario de pago
   * Nota: customOrderId siempre será igual a orderNumber
   */
  private updateFormFields(amountInCents: string, orderNumber: string, hash: string, customOrderId?: string): void {
    const amountInput = document.getElementById("Amount") as HTMLInputElement;
    const orderNumberInput = document.getElementById("OrderNumber") as HTMLInputElement;
    const authHashInput = document.getElementById("AuthHash") as HTMLInputElement;
    const customOrderIdInput = document.getElementById("CustomOrderId") as HTMLInputElement;

    const formValues: Record<string, string> = {};

    if (amountInput) {
      amountInput.value = amountInCents;
      formValues.Amount = amountInCents;
    }

    if (orderNumberInput) {
      orderNumberInput.value = orderNumber;
      formValues.OrderNumber = orderNumber;
    }

    if (authHashInput) {
      authHashInput.value = hash;
      formValues.AuthHash = hash;
    }

    // CustomOrderId debe ser el mismo valor que orderNumber
    const resolvedCustomOrderId = customOrderId || orderNumber;
    if (customOrderIdInput) {
      customOrderIdInput.value = resolvedCustomOrderId;
      formValues.CustomOrderId = resolvedCustomOrderId;
    }

    console.log("📝 Azul Payment - Valores establecidos en el formulario:", formValues);
  }

  /**
   * Procesa el pago redirigiendo a Azul con el monto especificado
   */
  public processPayment(paymentData: AzulPaymentData): void {
    const { amount, orderNumber, userId, branchId, customOrderId } = paymentData;

    console.log("🔵 Azul Payment - Datos recibidos:", {
      amount,
      orderNumber,
      userId,
      branchId,
      customOrderId,
      paymentData,
    });

    // Validar monto
    if (!amount || amount <= 0) {
      throw new Error("El monto debe ser mayor a 0");
    }

    // Obtener el formulario
    const form = document.getElementById(this.formId) as HTMLFormElement;
    if (!form) {
      throw new Error("No se encontró el formulario de pago");
    }

    // Convertir monto a centavos
    const amountInCents = this.convertToCents(amount);

    // Usar el orderNumber proporcionado o el del config
    const config = this.getConfig(userId, branchId, customOrderId);
    const finalOrderNumber = orderNumber || config.orderNumber;

    // CustomOrderId debe ser el mismo valor que orderNumber
    const finalCustomOrderId = finalOrderNumber;

    // Calcular hash de autenticación
    // Nota: CustomOrderId NO se incluye en el hash según la documentación de Azul
    const hash = this.calculateAuthHash(amountInCents, finalOrderNumber, userId, branchId, customOrderId);

    // Datos que se enviarán a Azul
    const dataToSend = {
      Amount: amountInCents,
      OrderNumber: finalOrderNumber,
      AuthHash: hash,
      CustomOrderId: finalCustomOrderId,
      MerchantId: config.merchantId,
      MerchantName: config.merchantName,
      MerchantType: config.merchantType,
      CurrencyCode: config.currencyCode,
      ITBIS: config.itbis,
      ApprovedUrl: config.approvedUrl,
      DeclinedUrl: config.declinedUrl,
      CancelUrl: config.cancelUrl,
      UseCustomField1: config.useCustomField1,
      UseCustomField2: config.useCustomField2,
      LogoImageUrl: config.logoImageUrl,
    };

    console.log("📤 Azul Payment - Datos enviados a Azul:", dataToSend);
    console.log("📤 Azul Payment - URL de destino:", this.azulPaymentUrl);

    // Actualizar campos del formulario
    this.updateFormFields(amountInCents, finalOrderNumber, hash, customOrderId);

    // Enviar formulario (redirige a Azul)
    form.submit();
  }

  /**
   * Obtiene la configuración del formulario para renderizarlo
   */
  public getFormConfig(userId?: string | null, branchId?: string | null, customOrderId?: string): AzulPaymentConfig {
    return this.getConfig(userId, branchId, customOrderId);
  }

  /**
   * Obtiene la URL de pago de Azul
   */
  public getPaymentUrl(): string {
    return this.azulPaymentUrl;
  }

  /**
   * Obtiene el ID del formulario
   */
  public getFormId(): string {
    return this.formId;
  }
}

export default AzulPaymentService;

